// Englische Fassung von ../de/questionnaires.ts.
//
// Unverändert übernommen (kein Übersetzungsfehler, sondern Absicht):
// `datumPlatzhalter` — das Datumsformat bleibt in jeder Sprache deutsch
// (docs/i18n.md §2), also auch sein Platzhalter. Die Werte „Yes"/„No"/„N/A"
// stehen gar nicht erst im Katalog: sie sind Dokumenten-Inhalt, keine
// Beschriftung (s. Kopf der deutschen Fassung).
import type { QuestionnairesKatalog } from "../de/questionnaires";

export const questionnaires: QuestionnairesKatalog = {
  // ── States + enums ────────────────────────────────────────────────────────
  status: {
    uploaded: "Waiting to be processed",
    profiling: "Checking the format",
    extracting: "Reading the questions",
    matching: "Looking for answers",
    review: "Ready for review",
    approved: "Reviewed",
    filled: "Filled in",
    rejected: "Not a questionnaire",
    error: "Error",
  },
  stufeMitZaehler: (stufe: string, erledigt: number, gesamt: number) =>
    `${stufe} — ${erledigt}/${gesamt}`,
  konfidenz: {
    high: "confident",
    medium: "medium",
    low: "uncertain",
  },
  ankerArt: {
    acroform: "PDF form field",
    docx_sdt: "Word content control",
    docx_checkbox: "Word check box",
    docx_table: "Word table cell",
    docx_paragraph: "Word paragraph",
    xlsx_cell: "Excel cell",
    pdf_overlay: "PDF stamp (geometry)",
    "xlsx-bereits-ausgefuellt": "already filled in",
  },

  alterDiesenMonat: "this month",
  alterMonate: (n: number) => (n === 1 ? "1 month old" : `${n} months old`),
  alterJahre: (n: number) => (n === 1 ? "1 year old" : `${n} years old`),

  // ── Overview (list + upload) ──────────────────────────────────────────────
  titel: "Questionnaires",
  beschreibung:
    "Upload customer questionnaires, have answers suggested, release every answer yourself.",
  listeFehlgeschlagen: "The questionnaires could not be loaded",
  hochgeladen: "Uploaded",
  wirdGelesen: (datei: string) => `${datei} is being read now.`,
  uploadFehlgeschlagen: "Upload failed",
  loeschenTitel: (datei: string) => `Delete “${datei}”?`,
  loeschenBeschreibung:
    "The run, all extracted questions and the uploaded customer file are removed.",
  loeschen: "Delete",
  geloescht: "Deleted",
  loeschenFehlgeschlagen: "Deleting failed",
  kunde: "Customer",
  kundePlatzhalter: "e.g. Mary Kay",
  produkt: "Product",
  produktHinweis: "Determines which product facts are used in the suggestions.",
  keinProduktbezug: "no product reference",
  ablegeZone: "Drag a questionnaire here or click",
  ablegeFormate: "PDF, DOCX or XLSX — please convert older .doc/.xls first.",
  dateiAria: "Select a questionnaire",
  wirdHochgeladen: "uploading …",
  laeufe: "Runs",
  wirdGeladen: "loading …",
  leerTitel: "No questionnaire uploaded yet",
  leerHinweis:
    "Upload the customer's questionnaire — the system reads the questions and suggests answers from the released library.",
  dateiWaehlen: "Select a file",
  fragenAnzahl: (n: number) => (n === 1 ? "1 question" : `${n} questions`),
  pruefen: "Review",
  fragebogenLoeschen: "Delete questionnaire",
  fragebogenLoeschenAria: (datei: string) => `Delete ${datei}`,

  // ── Review: messages ──────────────────────────────────────────────────────
  speichernFehlgeschlagen: "Saving failed",
  neuSuchenFehlgeschlagen: "Searching again failed",
  sektionFehlgeschlagen: "Changing the section failed",
  uebernehmenFehlgeschlagen: "Accepting failed",
  entscheidungFehlgeschlagen: "The decision failed",
  vorschlaegeUebernommen: (n: number) =>
    n === 1 ? "1 suggestion accepted" : `${n} suggestions accepted`,
  nurKonfidenzSicher: "Only confidence “confident”.",
  entscheidungGespeichert: "Decision saved",
  folgefragenGesetzt: (n: number, gelten: boolean) =>
    `${n} follow-up question(s) ${gelten ? "released" : "set to “not applicable”"}.`,
  ohneFolgefragen: "No follow-up questions in this questionnaire.",
  dokumentWirdAusgefuellt: "The document is being filled in",
  dokumentGefuellt: (n: number) =>
    `Original filled in — ${n} answer(s) written.`,
  dokumentGefuelltRest: (n: number) =>
    `${n} field(s) could not be filled automatically — please complete them by hand in the document.`,
  ausfuellenFehlt: "Filling in is not shipped yet",
  ausfuellenFehltDetail:
    "The endpoint /fill belongs to the writer package (P3) and is still missing on this server.",
  ausfuellenFehlgeschlagen: "Filling in failed",

  // ── Review: header + progress ─────────────────────────────────────────────
  nichtErreichbar: "Questionnaire not reachable",
  zurUebersicht: "To the overview",
  wirdGeladenLang: "The questionnaire is loading …",
  laufHinweis:
    "The review list appears once the run has finished. This page refreshes itself.",
  alleFragebogen: "All questionnaires",
  vorschlaegeModell: (modell: string) => `Suggestions: ${modell}`,
  geprueftVon: (reviewt: number, anwendbar: number) =>
    `${reviewt} of ${anwendbar} reviewed`,
  offenUndLuecken: (offen: number, luecken: number) =>
    `${offen} open · ${luecken} without a documented answer`,
  entscheidungenTeil: (n: number) => ` · ${n} decision(s)`,
  ausgeklammertTeil: (n: number) => ` · ${n} excluded`,
  lueckenTeil: (n: number) => ` · ${n} without a documented answer`,
  sichereUebernehmen: (n: number) =>
    n === 1 ? "Accept 1 confident suggestion" : `Accept ${n} confident suggestions`,

  // ── D-3: cells no question points to ──────────────────────────────────────
  offeneStellenTeil: (n: number) =>
    n === 1 ? " · 1 cell not processed" : ` · ${n} cells not processed`,
  offeneStellenTitel: (n: number) =>
    n === 1 ? "1 cell without a question" : `${n} cells without a question`,
  offeneStellenErklaerung:
    "These are answer cells and fields of the original document that no extracted question points to. They are NOT filled in automatically and have to be handled by hand.",
  offeneStellenWeitere: (n: number) => `… and ${n} more`,
  bereitsAusgefuelltTitel: (n: number) =>
    n === 1 ? "1 field already filled in" : `${n} fields already filled in`,
  bereitsAusgefuelltErklaerung:
    "These fields are already answered in the original document — by the other party, not by bitop. Nothing is missing here.",

  // ── Review: tabs + gaps ───────────────────────────────────────────────────
  reiterFragen: "Questions",
  reiterLuecken: "Gaps",
  reiterAnlagen: "Attachments",
  reiterDokument: "Document",

  // ── Overview: tabs + curated document list (WH 2026-08-21) ──
  reiterFragebogen: "Questionnaires",
  reiterDokumente: "Documents",
  dokRegistryHinweis:
    "The curated list of active supporting documents — filling pulls only from these. The files live in Salesforce; “Open SF” opens the record there.",
  dokOpenSf: "Open SF",
  dokSuche: "Search (title, ID) …",
  dokTypAlle: "All types",
  dokProduktAlle: "All products",
  dokGlobal: "global",
  dokNurAktive: "active only",
  dokAnzahl: (n: number, gesamt: number) => `${n} of ${gesamt} documents`,
  dokKeine: "No documents",
  dokKeineHinweis: "No entry matches the filters.",
  dokLadenFehler: "Could not load the document list",
  dokSpeichernFehler: "Could not save the change",
  dokAbgelaufen: "expired",
  dokAktiv: "active",
  dokInaktiv: "inactive",
  dokAktivUmschalten: "Pulled / not pulled — toggle",
  dokAktuellUmschalten: "“current” / “outdated” — toggle",
  dokSfPlatzhalter: "Salesforce link (https://…)",
  dokSfLabel: "Salesforce link",
  dokKeinSfLink: "no SF link",
  dokGueltigBisLabel: "valid until",
  lueckenHinweis:
    "These questions have no documented answer in the library. Answer them here by hand — the answer applies to this questionnaire only. Further actions (including searching again) sit behind the ⓘ on each row. (Documents whose release has to be decided appear as a decision next to the questions — not here.)",
  quelleEntwurf: "draft",
  quelleEntwurfTooltip: (frage: string) =>
    `Not curated yet (draft) — ${frage}. Suggestions based on it never become “safe” and are excluded from bulk approval.`,
  konfidenzGrund: (grund: string) => `Confidence capped: ${grund}`,

  keineLuecken: "No gaps",
  keineLueckenHinweis: "Every applicable question got a documented suggestion.",

  // ── Review: closing bar ───────────────────────────────────────────────────
  allesBearbeitet: (reviewt: number, luecken: number) =>
    `Everything processed — ${reviewt} approved, ${luecken} without a documented answer.`,
  bearbeitetMitOffenenStellen: (reviewt: number, luecken: number, stellen: number) =>
    `${reviewt} approved, ${luecken} without a documented answer · ${stellen} ${
      stellen === 1 ? "cell" : "cells"
    } not processed.`,
  nochOffen: (n: number) => `${n} still open`,
  dokumentAusfuellen: "Fill in the document",
  // „Fill in the document" steht hier bewusst gross geschrieben: mehrere Walks
  // selektieren diese Zeichenkette gross und ohne `i`-Flag (t11-abgelehnt.mjs).
  geprueftAusfuellen: "Reviewed — Fill in the document",
  geprueftAusfuellenTooltip:
    "Confirms the confident answers that are still open, then fills in the document. Medium, uncertain and undocumented questions still need an individual look.",
  attestierungHinweis:
    "Accepted as part of your review. Everything else stays open and still needs an individual look.",

  // ── Document tab: view the filled document + download the package ──────────
  dokumentReiterHinweis: "The filled document, exactly as it will be submitted.",
  dokumentLaedt: "Generating preview …",
  dokumentLeerTitel: "Not filled in yet",
  dokumentLeerHinweis: "Fill in the document to view it here.",
  dokumentFehlerTitel: "Preview not available",
  dokumentNochNicht: "There is no filled version yet — please fill in the document first.",
  dokumentNeuFuellen: "Fill in again",
  paketDownload: "Download (package)",
  paketDatei: "-package.zip",
  paketLeer: "Nothing to download",
  paketFehlgeschlagen: "Download failed",

  // ── D-9: rejected questionnaire ───────────────────────────────────────────
  abgelehntTitel: "This questionnaire was rejected",
  abgelehntZeile: "Rejected — this document will not be filled in.",
  abgelehntKeinAusfuellen:
    "A rejected document is not filled in — please upload the actual questionnaire.",

  // ── Known capability gaps (`format_profile.unsupported`) ──────────────────
  // The messages themselves arrive pre-formatted (German) from profiler.py and
  // stay untranslated — they name file paths and concept paragraphs. Only the
  // frame around the list is translated.
  einschraenkungenTitel: "Known limitations of this document",
  einschraenkungenHinweis:
    "The questionnaire is still filled in — but the system cannot read or write back these parts. Please check them by hand.",

  // ── Review: sections ──────────────────────────────────────────────────────
  ohneSektion: "Without a section",
  sektionGeprueft: (geprueft: number, gesamt: number) => `${geprueft}/${gesamt} reviewed`,
  giltNicht: "does not apply to bitop",
  giltNichtSetzen: "Does not apply to bitop",
  wiederAufnehmen: "Include again",
  sektionAusgeklammert: (grund: string) => `Excluded by the extraction: ${grund}`,
  sektionVonHand:
    "Excluded by hand — the questions stay empty in the document (concept B4). “Include again” undoes that.",

  // ── Review: decision block ────────────────────────────────────────────────
  entscheidungNoetig: (position: number) => `Decision needed — question #${position}`,
  entscheidungsKontext: "Decision Context",
  kanonFrageZeile: (frage: string) => `Canonical question: “${frage}”`,
  freitextCommercial: "Free text — the commercial team decides on the wording.",
  folgefragenBleiben: "Follow-up questions remain to be answered",
  folgefragenBleibenJa: "The dependent questions stay in the list.",
  folgefragenBleibenNein:
    "The dependent questions are excluded as “not applicable”.",
  notizPlatzhalter: "Note on the decision (optional) — e.g. who made it",
  entscheidungSpeichern: "Save the decision",

  // ── Review: contact roles ─────────────────────────────────────────────────
  kontaktFrage: (rolle: string) =>
    `Who is ${rolle} for this questionnaire?`,
  kontaktRollen: {
    commercial: "the commercial contact",
    quality: "the quality contact",
    technical: "the technical contact",
    ceo: "the managing director",
    signer: "the signatory",
  },
  kontaktFeld: {
    name: "Name",
    titel: "Position / function",
    email: "E-mail",
    telefon: "Phone",
  },
  kontaktHinweis:
    "Pre-filled from the fact sheet — please check and overwrite where needed. The system does not suggest anything here from the answer library.",
  kontaktZielFelder: (liste: string) =>
    `Written into this form field: ${liste}`,
  kontaktFehlendeFakten: (liste: string) => `Not recorded in the fact sheet: ${liste}`,
  kontaktUebernehmen: "Save the contact",

  // ── Question card: input ──────────────────────────────────────────────────
  jaNeinHinweis:
    "Yes / No / N/A are written verbatim into the document — they are the answer value, not a label.",
  eigenerText: "or your own text",
  bitteWaehlen: "— please choose —",
  datumPlatzhalter: "TT.MM.JJJJ",

  // ── Question card: sources + state ────────────────────────────────────────
  kanonFrageTooltip: (frage: string) => `Canonical question: ${frage}`,
  faktAusFaktenblatt: "Fact from the fact sheet",
  korrigiert: "corrected",
  uebernommen: "accepted",
  ausgelassen: "skipped",
  ankerFehltKurz: "cannot be inserted automatically",
  keinAnkerTooltip:
    "Without an anchor the answer can only be exported, not written into the document.",
  korrekturSpeichern: "Save the correction",
  abbrechen: "Cancel",
  /** yes_no_text: placeholder for the detail field next to Yes/No. */
  spezifikationPlatzhalter: "Detail (e.g. frequency, temperature) …",
  /** "Do you have X? If yes, attach Y": attachment marker next to the Yes/No. */
  anlageMarker: (code: string, titel: string): string => {
    switch (code) {
      case "anlage_beilegen":
        return titel ? `Attach document: ${titel}` : "Attach document";
      case "anlage_vertraulich":
        return titel ? `Document (release needed): ${titel}` : "Document — release needed";
      case "anlage_abgelaufen":
        return titel ? `Document expired: ${titel}` : "Document expired";
      case "anlage_fehlt":
        return "Document missing — decide on release";
      default:
        return titel ? `Check document: ${titel}` : "Check document";
    }
  },
  herkunftKorrigiert: "corrected",
  herkunftEntschieden: "decided",
  herkunftVorschlag: "suggestion accepted",
  keineBelegteAntwort: "No documented answer.",
  normKonflikt: (quelle: string, frage: string) =>
    `The cited source is about ${quelle}, the question asks about ${frage} — an answer about a different standard does not document this question.`,
  naechsterKandidat: (frage: string, seed: string, score: string, schwelle: string) =>
    `Closest match: “${frage}” (${seed}${score}) — below the threshold ${schwelle}.`,
  entscheidetCommercial:
    "The commercial team decides this question — the system does not pre-fill it.",
  entscheiden: "Decide",
  nochNichtGesucht: "No search has been run for this question yet.",
  fehlendeFakten: (liste: string) => `Facts missing from the fact sheet: ${liste}`,
  standDerFakten: (liste: string) => `State of the facts used: ${liste}`,
  faktStand: (datum: string, wieAlt: string) =>
    wieAlt ? `As of ${datum} (${wieAlt})` : `As of ${datum}`,
  faktOffen: (todo: string) => `open: ${todo}`,
  verworfeneQuellen: (liste: string) => `Discarded (undocumented) source references: ${liste}`,

  // ── Question row: one click, long version, details ────────────────────────
  vorschlagTooltip: "The system's suggestion — one click accepts it.",
  sichererVorschlagTooltip:
    "Confident answer from the library — it is accepted when you click “Reviewed — Fill in the document”. “Change” opens it.",
  antwortEintragen: "Enter an answer",
  antwortBearbeitenTooltip: "Click to edit",
  konfidenzPunktTooltip: (stufe: string) => `Confidence: ${stufe}`,
  details: "Details",
  detailsTooltip: "Sources, caveats and further actions for this question",
  langfassung: "Long version",
  langfassungTitel: "Additional detail",
  kanonFrageQuelle: (quelle: string, frage: string) => `${quelle} — “${frage}”`,

  // ── Review: section jump bar ──────────────────────────────────────────────
  sprungleisteTitel: "Sections",
  sprungZiel: (name: string, geprueft: number, gesamt: number) =>
    `Jump to “${name}” — ${geprueft} of ${gesamt} reviewed`,

  // ── Question row: actions ─────────────────────────────────────────────────
  uebernehmen: "Accept",
  aendern: "Change",
  korrigieren: "Correct",
  auslassen: "Skip",
  zuruecksetzen: "Reset",
  neuSuchen: "Search again",
  neuSuchenTooltip:
    "Queries the answer library again — e.g. after an entry has been added.",

  // ── Attachment checklist ──────────────────────────────────────────────────
  lageVorhanden: "Document available",
  lagePruefen: "check",
  lageFehlt: "no document",
  keineAnlagenTitel: "This questionnaire does not request any attachments",
  keineAnlagenHinweis:
    "The system recognises attachment questions by fields of type “Attachment” and by wordings such as “… included?” or “… please provide a copy”.",
  belegFragen: (n: number) =>
    n === 1 ? "1 attachment question:" : `${n} attachment questions:`,
  zaehlerVorhanden: (n: number) => `${n} available`,
  zaehlerPruefen: (n: number) => `${n} to check`,
  zaehlerFehlt: (n: number) => `${n} missing`,
  registryGefiltert: (produkt: string, belege: number) =>
    `Registry filtered to ${produkt} · ${belege} documents`,
  registryGlobal: "global",
  abgleichHinweis:
    "This comparison is a suggestion: it matches the question against the title, type and topics of the document. Please look at the named document yourself before the tick goes into the form.",
  aktuellTooltip: "Most recent version according to the registry",
  aktuell: "current",
  veraltetTooltip: "There is a newer version",
  veraltet: "outdated",
  ausgestellt: (datum: string) => `issued ${datum}`,
  gueltigBis: (datum: string) => `· valid until ${datum}`,
  abgelaufen: " — expired",
  vertraulich: "Confidential.",
  uebereinstimmung: (score: string) => `· match ${score}`,
  belegeBeilegen: (n: number) => `Documents to attach (${n})`,
  weitereKandidaten: "Further candidates:",
  weitereVertraulich: " — confidential",
  keinBelegHinweis:
    "There is no matching document for this product in the registry. Either the document really is missing — or it has not been registered yet.",
  vertraulichHinweis:
    "This document is marked confidential. The system therefore does NOT promise it automatically — please clear it before sending.",
  anlagenAbgelehntTitel: (n: number) => `Denied / not applicable (${n})`,
  anlagenAbgelehntHinweis:
    "These attachment questions are answered \"No\" or \"N/A\" — the documents are not included with the submission.",
  inSalesforceOeffnen: "Open in Salesforce",
  eigeneAnlagenTitel: "Add attachments",
  eigeneAnlagenHinweis:
    "Attach missing documents here by drag & drop — they go into the download package.",
  anlageAblegen: "Drop a file here or click",
  anlageWirdHochgeladen: "uploading …",
  anlageHochgeladen: (name: string) => `"${name}" attached`,
  anlageUploadFehlgeschlagen: "Could not upload the attachment",
  anlageEntfernen: "Remove attachment",
  anlageLoeschenFehlgeschlagen: "Could not remove the attachment",

  // ── K6: deterministic reasons (grund_code) ────────────────────────────────
  grundAnlageFehlt: "No document of this kind in the attachment registry.",
  grundAnlageUnsicher: (titel: string, score: string) =>
    `Document only presumed: "${titel}" matches at ${score} — please check.`,
  grundAnlageAbgelaufen: (titel: string, datum: string) =>
    `"${titel}" expired on ${datum} — renew before sending.`,
  grundAnlageVertraulich: (titel: string) =>
    `"${titel}" is available but marked confidential — clearance is required before promising it.`,
  // `position` is the DISPLAYED card number in every text here, not the
  // 0-based `q_question.position` — see the German source catalogue.
  grundGatingElternantwort: (position: number, antwort: string) =>
    `Follow-up to question #${position}, which says "${antwort}". This question therefore does not apply; the suggested answer is "N/A".`,
  grundGatingSektion: (praefix: string, position: number, antwort: string) =>
    `Section ${praefix} only applies with a different answer to question #${position} ("${antwort}").`,
  grundSignoffSignatur:
    "Stamp field — left empty for now. The document is stamped, not signed; the stamp is applied by the upcoming signature solution and is never pre-filled.",
  grundSignoffTitelFehlt: (name: string) =>
    `No job title is stored for ${name} (user profile) — please add it or fill the field manually.`,
  grundSignoffKeinNutzer:
    "There is no user record for the uploader — name and job title cannot come from the system.",
  grundAnlageZuordnungMittel: (titel: string, score: string) =>
    `Document match is only moderate (${score}) — look at "${titel}" yourself before promising it.`,

  // ── K6: origin of a machine-decided answer ────────────────────────────────
  herkunftRegistry: "from the attachment registry",
  herkunftSystem: "from the system state",
  herkunftRegistryTooltip:
    "This answer follows from the stock of the attachment registry — not from a library answer and not from a language model.",
  herkunftSystemTooltip:
    "This answer follows from the signed-in user and the fill-in date — not from a library answer and not from a language model.",

  // ── K7: master data straight from the fact sheet ──────────────────────────
  herkunftFaktenblatt: (key: string, stand: string) =>
    stand ? `Fact sheet: ${key} (as of ${stand})` : `Fact sheet: ${key}`,
  herkunftFaktenblattTooltip:
    "This answer is the fact sheet value itself — no retrieval hit and no phrased sentence. What needs checking is the value, not the wording.",
  grundStammdatenOffenerTodo: (key: string) =>
    `The fact sheet has an open point on ${key} — that is why this is not "safe". The open point is shown below.`,
  gatingHinweis: (position: number, text: string) =>
    `Follow-up to #${position}: "${text}"`,

  // ── F6/B1: decision inline in the row ─────────────────────────────────────
  entscheidungOffen: "Decision pending",
  entscheidungAufklappen: "Expand to decide",
  entscheidungEinklappen: "Collapse",
  belegHerausgabeFrage: "Provide to this customer?",
  belegHerausgabeFehlt:
    "There is no standard document on file for this request. Whether the document is produced and released for this customer is a decision.",
  belegHerausgabeVertraulich: (titel: string) =>
    `“${titel}” is on file but marked confidential. Releasing it to this customer is an approval decision.`,

  // ── F6/B2: contact from the employee directory ────────────────────────────
  verzeichnisWaehlen: "Choose from directory …",
  verzeichnisOption: (name: string, zusatz: string) =>
    zusatz ? `${name} · ${zusatz}` : name,
  verzeichnisHinweis:
    "Pick a person from the employee directory — the fields are filled in and stay editable.",

  // ── F6/B3: revenue from Salesforce (decision) ─────────────────────────────
  umsatzTitel: "Revenue from Salesforce",
  umsatzEmpfehlung: (wert: string) => `Recommended revenue: ${wert}`,
  umsatzBasisScenario: (jahr: number, eur: string, usd: string) =>
    `Scenario Master ${jahr} (current RFC — finished goods + licenses + medical raw): ${eur} EUR ≈ ${usd} USD.`,
  umsatzBasisFakt: (wert: string) =>
    `Salesforce unavailable — revenue figure from the fact sheet (${wert}).`,
  umsatzFreigabeHinweis:
    "Disclosing the revenue figure is also a commercial-approval decision — please confirm.",
  umsatzUebernehmen: "Insert recommendation",

  // ── F6/B8: second bulk action ─────────────────────────────────────────────
  alleAbhaken: "Tick off everything else",
  alleAbhakenTooltip:
    "Sets all remaining open suggestions (any confidence, drafts included) to “reviewed” and the remaining open questions to “skipped”. Decisions and gaps stay untouched — it goes through the final check anyway.",
  abhakenFehlgeschlagen: "Ticking off failed",
  allesAbgehakt: (uebernommen: number, uebersprungen: number) =>
    `${uebernommen} accepted · ${uebersprungen} skipped`,

  // ── F6/B9: verbatim duplicates in the form ────────────────────────────────
  duplikatHinweis: (position: number) =>
    `Identical to question #${position} — duplicated in the form.`,
  duplikatUebernehmen: (position: number) => `Copy answer from #${position}`,
};
